import React, {useEffect, useState} from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { faBlind, faDeaf, faWheelchair } from '@fortawesome/free-solid-svg-icons';
import PhoneIcon from '@mui/icons-material/Phone';
import MailIcon from '@mui/icons-material/Mail';
import LanguageIcon from '@mui/icons-material/Language';
import PinDropIcon from '@mui/icons-material/PinDrop';
import WebAssetIcon from '@mui/icons-material/WebAsset';

import { setContentType, setOpenSideBar } from 'handimap-react-lib/store/actions/sideBarActions';
import { useMarker } from 'handimap-react-lib/utils/useMarker';
import { callGetFeatureJson } from 'handimap-react-lib/api/jsonFeatureApi';
import { setAlert } from 'handimap-react-lib/store/actions/alertActions';
import { unselectFeature } from 'handimap-react-lib/store/actions/selectedFeatureActions';
import { FontAwesomeSvgIcon } from 'handimap-react-lib/components/FontAwesomeSvgIcon';
import { CustomButtonItinFeature } from 'handimap-react-lib/components/Button/CustomButton';
import { FeatureTextItemsList } from 'handimap-react-lib/components/Select/CustomListItem';
import { FeatureTypography } from 'handimap-react-lib/components/Typography/FeatureTypography';
import SvgS3AIcon from 'handimap-react-lib/components/icons/mentIcon';

import { AccessLibreContainer } from '../../containers/AccessLibreContainer';
import { AccessLibreLink } from '../../containers/AccessLibreLink';

export const SelectedFeaturePointDetails = ({ 
  isMobile, 
  apiFeature, 
  layerID, 
  getFeatureInfoAttrs
}) => {

  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {setMarker} = useMarker();


  const [location, setLocation] = useState([]);

  const [featureTitle, setFeatureTitle] = useState(null);
  const [featureDescription, setFeatureDescription] = useState(null);
  const [featureAdresse, setFeatureAdresse] = useState(null);
  const [featurePhone, setFeaturePhone] = useState(null);
  const [featureEmail, setFeatureEmail] = useState(null);
  const [featureWebsite, setFeatureWebsite] = useState(null);
  const [featureDetailsUrl, setFeatureDetailsUrl] = useState(null);
  const [featureAccess, setFeatureAccess] = useState(null);
  const [featureDictAccess, setFeatureDictAccess] = useState({});

  const locationUndefined = location[0] === undefined || location[1] === undefined;
  
  const goBack = async () => {
    dispatch(unselectFeature());
    dispatch(setOpenSideBar(false));
    // add a timout for mobile mode to avoid strange transition
    if (isMobile) {
      await new Promise(res => setTimeout(res, 50));
    }
    dispatch(setContentType('direction'));
  };

  const fetchInfoFeature = (idFeature, idLayer) => {
    callGetFeatureJson(idLayer, idFeature).then(
      result => {
        const {geometry} = result;
        if (geometry.type === 'MultiPoint') {
          setLocation(geometry.coordinates[0]);
        } else {
          setLocation(geometry.coordinates);
        }
      }
    ).catch(error => {
      console.log('error', error); 
      setLocation([]);
    });
  };


  const handleButtonClick = (dest) => {
    if(location.length === 0) {
      dispatch(setAlert(t(`error.error-place-${dest}-feature`), 'error', t('error.label'), 'alert'));
    }
    setMarker(location, dest);
    goBack();
  }; 

  const getColorAndLabelAccForAttr = (accAttr, valueColorMapping, handicapLabel) => {
    const selectedFeatureAccValue = String(apiFeature.properties[accAttr]);
    if (Object.keys(valueColorMapping).includes(selectedFeatureAccValue)) {
      return {
        color: valueColorMapping[selectedFeatureAccValue].color,
        label: `${valueColorMapping[selectedFeatureAccValue].label} ${handicapLabel}`
      }; 
    } else {
      return {
        color: '#9a9a9a',
        label: `${t('selectedFeature.not-spec')} ${handicapLabel}`
      }; 
    }
  }; 

  useEffect(() => {
    const featureTitleAttr = getFeatureInfoAttrs[layerID].title;
    const tempTitle = apiFeature.properties[featureTitleAttr];
    const title = tempTitle && tempTitle !== '' ? tempTitle: getFeatureInfoAttrs[layerID].defaultTitle;

    const featureDescriptionAttr = getFeatureInfoAttrs[layerID].description;
    const description = Object.keys(apiFeature.properties).includes(featureDescriptionAttr) ? apiFeature.properties[featureDescriptionAttr] : null;
    if (Object.keys(getFeatureInfoAttrs[layerID]).includes('acc_info')) {
      var valueColorMapping = {};
      // map acc values with picto and label, use acc values as key
      valueColorMapping[String(getFeatureInfoAttrs[layerID].acc_info.acc_value)] = {
        color: '#6ba328',
        label: t('selectedFeature.acc')
      };
      valueColorMapping[String(getFeatureInfoAttrs[layerID].acc_info.part_acc_value)] = {
        color: '#d4901e',
        label: t('selectedFeature.part-acc')
      };
      valueColorMapping[String(getFeatureInfoAttrs[layerID].acc_info.not_acc_value)] = {
        color: '#f00025',
        label: t('selectedFeature.not-acc')
      };
      // init final result
      var finalResult = {};
      // Get acc for wheelchair and push it in the final result
      const wheelchairAccAttr = getFeatureInfoAttrs[layerID].acc_info.wheelchair;
      finalResult['wheelchair'] = getColorAndLabelAccForAttr(wheelchairAccAttr, valueColorMapping, t('selectedFeature.wheelchair'));
      finalResult['wheelchair']['faIcon'] = <FontAwesomeSvgIcon ariaLabel={t('selectedFeature.profile-wheelchair')} role='img' icon={faWheelchair} ariaHidden={false} />;
      // Get acc for visually and push it in the final result
      const visuallyAccAttr = getFeatureInfoAttrs[layerID].acc_info.visually;
      finalResult['visually'] = getColorAndLabelAccForAttr(visuallyAccAttr, valueColorMapping, t('selectedFeature.visually'));
      finalResult['visually']['faIcon'] = <FontAwesomeSvgIcon ariaLabel={t('selectedFeature.profile-visual')} role='img' icon={faBlind} ariaHidden={false} />;
      // Get acc for hearing and push it in the final result
      const hearingAccAttr = getFeatureInfoAttrs[layerID].acc_info.hearing;
      finalResult['hearing'] = getColorAndLabelAccForAttr(hearingAccAttr, valueColorMapping, t('selectedFeature.hearing'));
      finalResult['hearing']['faIcon'] = <FontAwesomeSvgIcon ariaLabel={t('selectedFeature.profile-hearing')} role='img' icon={faDeaf} ariaHidden={false} />;
      // Get acc for mentally and push it in the final result
      const mentallyAccAttr = getFeatureInfoAttrs[layerID].acc_info.mentally;
      finalResult['mentally'] = getColorAndLabelAccForAttr(mentallyAccAttr, valueColorMapping, t('selectedFeature.mentally'));
      finalResult['mentally']['faIcon'] = <SvgS3AIcon aria-label={t('selectedFeature.profile-mental')} aria-hidden={false}/>;
      
      setFeatureDictAccess(finalResult);
      setFeatureAccess(getFeatureInfoAttrs[layerID]['acc_info']);
    } else {
      setFeatureDictAccess({});
      setFeatureAccess(null);
    }
    setFeatureTitle(title);
    setFeatureDescription(description);
    if (apiFeature.properties.adresse1 || apiFeature.properties.commune) {
      setFeatureAdresse(`${apiFeature.properties.adresse1}, ${apiFeature.properties.commune}`);
    } else {
      setFeatureAdresse(null);
    }
    setFeaturePhone(apiFeature.properties.telfixe);
    setFeatureEmail(apiFeature.properties.email);
    setFeatureWebsite(apiFeature.properties.siteweb);
    setFeatureDetailsUrl(apiFeature.properties.detailurl);
    fetchInfoFeature(apiFeature.properties.fid, layerID);
    return () => {
      setFeatureTitle(null);
    };
  }, [apiFeature, t]);

  const style = {
    container: {
      width: '100%',
      alignItems: 'center',
    },
    headerContainer: {
      display: 'flex',
      width: '100%',
      paddingX: '0.5em',
      boxSizing: 'border-box',
    },
    featureLabel: {
      width: '95%', 
      fontSize: '1.2em', 
      alignSelf:'center'
    },
    cardContent: {
      paddingX: '0 !important',
    },
    card: {
      display: 'flex',
      width: '100%',
      alignSelf: 'center',
      paddingX: '0.5em',
      boxSizing: 'border-box',
      backgroundColor: 'transparent'
    },
    buttonContainer: {
      display: 'flex', 
      width: '100%', 
      paddingX: '.5em',
      boxSizing: 'border-box',
      backgroundColor: 'rgb(237,127,21,0.12)',
      paddingY: '1em'
    },
    listIcon: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '2.2em',
      height: '2.2em',
      minWidth: '1em',
      marginRight: '.8em',
      padding: '0.5em',
      borderRadius : '100vmax',
    },
    featureText: {
      fontSize: '1em', 
      whiteSpace: 'pre-line'
    },
    infoText: {
      fontSize: '1em', 
      color: '#000000',
      alignSelf: 'center'
    },
    iconStyle: {
      color: '#000000', 
      fontSize: '2em'
    },
    descContainer: {
      display: 'flex',
      paddingX: '.5em'
    }
  };
  
  return (
    <Stack sx={style.container} direction='column' spacing={2}>
      <Box sx={style.headerContainer}>
        <FeatureTypography variant="h1" sx={style.featureLabel}>
          {apiFeature.properties.activite ? `${apiFeature.properties.activite}: ` : ''} {featureTitle}
        </FeatureTypography>
      </Box>
      {featureDescription && (
        <Box sx={style.descContainer}>
          <FeatureTypography
            component="p" 
            sx={{ 
              fontSize: '1em', 
              color: 'white' }}>
            {featureDescription}
          </FeatureTypography>
        </Box>
      )      
      }
      {(featureAdresse || featurePhone || featureEmail || featureWebsite || featureDetailsUrl) &&
        <Card sx={style.card} elevation={0}>
          <CardContent sx={style.cardContent}>
            {featureAdresse &&
              <Stack direction="row" spacing={2}>
                <PinDropIcon sx={style.iconStyle}/>
                <FeatureTypography component="p" sx={style.infoText}>
                  {featureAdresse}
                </FeatureTypography>
              </Stack>
            }
            { featureDetailsUrl && 
              <Stack direction="row" spacing={2}>
                <WebAssetIcon sx={style.iconStyle}/>
                <FeatureTypography component="p" sx={style.infoText}>
                  <a target="_blank" rel="noopener noreferrer" href={featureDetailsUrl}> {t('selectedFeature.details-url')}</a>
                </FeatureTypography>
              </Stack>
            }
            { featurePhone && 
              <Stack direction="row" spacing={2}>
                <PhoneIcon sx={style.iconStyle}/>
                <FeatureTypography component="p" sx={style.infoText}>
                  <a target="_blank" rel="noopener noreferrer" href={`tel:${featurePhone}`}> {featurePhone}</a>
                </FeatureTypography>
              </Stack>
            }
            { featureEmail &&
            <Stack direction="row" spacing={2}>
              <MailIcon sx={style.iconStyle}/>
              <FeatureTypography component="p" sx={style.infoText}>
                <a target="_blank" rel="noopener noreferrer" href={`mailto:${featureEmail}`}> {featureEmail}</a>
              </FeatureTypography>
            </Stack>
            }
            { featureWebsite && 
              <Stack direction="row" spacing={2}>
                <LanguageIcon sx={style.iconStyle}/>
                <FeatureTypography component="p" sx={style.infoText}>
                  <a target="_blank" rel="noopener noreferrer" href={featureWebsite}> {t('selectedFeature.site-url')}</a>
                </FeatureTypography>
              </Stack>
            }
          </CardContent>
        </Card>
      }
      {featureAccess && 
          <Card sx={style.card} elevation={0}>
            <CardContent sx={style.cardContent}>
              <Box sx={{ display: 'flex', width: '100%' }}>
                <List dense={true}>
                  {
                    Object.keys(featureDictAccess).map((key) => {
                      return (
                        <ListItem key={key} sx={{paddingX: '0 !important'}}>
                          <ListItemIcon 
                            sx={{
                              ...style.listIcon , 
                              borderRight: `2.5px solid ${featureDictAccess[key].color}`,
                              borderBottom: `2.5px solid ${featureDictAccess[key].color}`,
                              color: featureDictAccess[key].color
                            }}
                          >
                            {featureDictAccess[key].faIcon}
                          </ListItemIcon>
                          <FeatureTextItemsList
                            primary={featureDictAccess[key].label}
                            disableTypography={true}
                            sx={style.featureText}
                          />
                        </ListItem>
                      );
                    }
                    )
                  }
                </List>
              </Box>
            </CardContent>
          </Card>
      }
      
      <Stack 
        sx={style.buttonContainer} 
        direction='column' 
        spacing={2}
      >
        <FeatureTypography variant="h6" sx={{ fontSize: '.8em' }}>
          {t('selectedFeature.use-point-as')}
        </FeatureTypography>
        <CustomButtonItinFeature 
          id='button-set-origin'
          disabled={locationUndefined}
          startIcon={null}
          onClick={() => handleButtonClick('origin')}
        >
          {t('popup.deaparture')}
        </CustomButtonItinFeature>
        <CustomButtonItinFeature 
          id='button-set-destination'
          disabled={locationUndefined}
          startIcon={null}
          onClick={() => handleButtonClick('destination')}
        >
          {t('popup.arrival')}
        </CustomButtonItinFeature>
      </Stack>
      {apiFeature && apiFeature.properties.uuid && (
        <AccessLibreLink uuid={apiFeature.properties.uuid} />
      )}
      {isMobile && apiFeature && apiFeature.properties.uuid && (
        <AccessLibreContainer />
      )}
    </Stack>
  );
};